import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

function toTitleCase(value) {
  return String(value || "")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function normalizeCity(slug) {
  const raw = String(slug || "").trim().toLowerCase();
  if (!raw) return "Nepal";

  const aliases = {
    ktm: "Kathmandu",
    kathmandu: "Kathmandu",
    lalitpur: "Lalitpur",
    bhaktapur: "Bhaktapur",
    pokhara: "Pokhara",
    chitwan: "Chitwan",
    butwal: "Butwal",
    dharan: "Dharan",
  };

  return aliases[raw] || toTitleCase(raw.replace(/-/g, " "));
}

const TARGET_CITIES = [
  "kathmandu",
  "lalitpur",
  "bhaktapur",
  "pokhara",
  "chitwan",
  "butwal",
  "dharan",
];

const CATEGORY_MAP = {
  gharbhada: {
    label: "Gharbhada",
    listingType: "",
    ctaLabel: "See Rentals",
    intro: (city) => `Browse verified gharbhada options in ${city} with rent, amenities, and neighborhood details in one place.`,
  },
  "flat-bhada": {
    label: "Flat Bhada",
    listingType: "Apartment",
    ctaLabel: "See Flat Listings",
    intro: (city) => `Explore flat bhada and apartment rent options in ${city} with direct owner contact and verified details.`,
  },
  "room-rent": {
    label: "Room Rent",
    listingType: "Room",
    ctaLabel: "See Room Listings",
    intro: (city) => `Find room rent options in ${city} for students, professionals, and families by budget and location.`,
  },
  "house-for-rent": {
    label: "House for Rent",
    listingType: "House",
    ctaLabel: "See House Listings",
    intro: (city) => `Compare house for rent listings in ${city} with bedrooms, parking, and monthly rent details.`,
  },
  "apartment-rent": {
    label: "Apartment Rent",
    listingType: "Apartment",
    ctaLabel: "See Apartment Listings",
    intro: (city) => `Discover apartment rent listings in ${city} with furnished and unfurnished options for every budget.`,
  },
  "kotha-bhada": {
    label: "Kotha Bhada",
    listingType: "Room",
    ctaLabel: "See Kotha Listings",
    intro: (city) => `Search kotha bhada in ${city} with nearby landmarks, commute options, and monthly rent range.`,
  },
  "jagga-bhada": {
    label: "Jagga Bhada",
    listingType: "Land",
    ctaLabel: "See Jagga Listings",
    intro: (city) => `Find jagga bhada and land lease options in ${city} with location and rental terms.`,
  },
  "office-space-rent": {
    label: "Office Space Rent",
    listingType: "Commercial",
    ctaLabel: "See Commercial Listings",
    intro: (city) => `Browse office and commercial rental listings in ${city} with floor area, access, and monthly pricing.`,
  },
};

const VARIANTS = {
  gharbhadaCity: {
    heading: (city) => `Gharbhada in ${city}`,
    lead: (city) => `Browse verified rental homes, flats, and rooms in ${city}. Compare rent, amenities, and location before you contact owners directly.`,
    type: "",
    ctaLabel: "See Rentals",
  },
  flatBhadaCity: {
    heading: (city) => `Flat Bhada in ${city}`,
    lead: (city) => `Find apartment and flat bhada listings in ${city} with monthly rent, furnished options, and verified owner details.`,
    type: "Apartment",
    ctaLabel: "See Flat Listings",
  },
  roomRentCity: {
    heading: (city) => `Room Rent in ${city}`,
    lead: (city) => `Explore room bhada options in ${city} for students, professionals, and families with direct owner contact options.`,
    type: "Room",
    ctaLabel: "See Room Listings",
  },
  cheapKothaNearCollege: {
    heading: () => "Cheap Kotha Bhada Near College",
    lead: () => "Find budget-friendly room bhada options near major college and university areas in Nepal with lower monthly rent options.",
    type: "Room",
    maxPrice: 20000,
    city: "Kathmandu",
    ctaLabel: "See Budget Rooms",
  },
  cityHub: {
    heading: (city) => `Rent in ${city}`,
    lead: (city) => `Explore popular rental categories in ${city} including gharbhada, flat bhada, room rent, and house for rent with verified listing options.`,
    ctaLabel: "Explore Rentals",
  },
  rentCategory: {
    heading: () => "",
    lead: () => "",
    ctaLabel: "See Rentals",
  },
};

function SeoLandingPage({ variant }) {
  const navigate = useNavigate();
  const { citySlug, categorySlug } = useParams();
  const config = VARIANTS[variant] || VARIANTS.gharbhadaCity;

  const parsedCategory = useMemo(() => {
    if (variant === "flatBhadaCity") return "flat-bhada";
    if (variant === "roomRentCity") return "room-rent";
    if (variant === "gharbhadaCity") return "gharbhada";
    if (variant === "cheapKothaNearCollege") return "kotha-bhada";
    if (variant === "rentCategory") return String(categorySlug || "").toLowerCase();
    return "";
  }, [variant, categorySlug]);

  const parsedCity = useMemo(() => normalizeCity(config.city || citySlug), [config.city, citySlug]);
  const categoryConfig = CATEGORY_MAP[parsedCategory] || null;
  const isCityHub = variant === "cityHub";
  const city = parsedCity;
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);

  const heading = useMemo(() => {
    if (isCityHub) return config.heading(city);
    if (categoryConfig) return `${categoryConfig.label} in ${city}`;
    return config.heading(city);
  }, [categoryConfig, city, config, isCityHub]);

  const lead = useMemo(() => {
    if (isCityHub) return config.lead(city);
    if (categoryConfig) return categoryConfig.intro(city);
    return config.lead(city);
  }, [categoryConfig, city, config, isCityHub]);

  const listingType = categoryConfig?.listingType || config.type || "";

  const faqItems = useMemo(() => {
    const categoryLabel = categoryConfig?.label || "Rental";
    return [
      {
        q: `What is the average ${categoryLabel.toLowerCase()} in ${city}?`,
        a: `Average rent depends on area, amenities, and furnishing. Compare recent listings and shortlist verified options before contacting owners.`,
      },
      {
        q: `How do I find verified rentals in ${city}?`,
        a: `Use filters for type, location, and budget, then check listing details and owner response quality before finalizing.`,
      },
      {
        q: `Can I find budget-friendly options in ${city}?`,
        a: `Yes. Use max rent filters and compare neighborhoods to find lower-cost rooms, flats, or houses.`,
      },
    ];
  }, [categoryConfig?.label, city]);

  useEffect(() => {
    let isMounted = true;

    async function fetchListings() {
      try {
        const response = await fetch("/api/properties");
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];

        const filtered = list
          .filter((item) => {
            const itemCity = String(item?.city || "").toLowerCase();
            const matchesCity = city === "Nepal" || itemCity.includes(city.toLowerCase());
            const matchesType = !listingType || String(item?.type || "").toLowerCase() === listingType.toLowerCase();
            const matchesPrice = !config.maxPrice || Number(item?.price) <= config.maxPrice;
            return matchesCity && matchesType && matchesPrice;
          })
          .slice(0, 6);

        if (isMounted) {
          setResults(filtered);
        }
      } catch {
        if (isMounted) setResults([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchListings();
    return () => {
      isMounted = false;
    };
  }, [city, config.maxPrice, listingType]);

  const params = new URLSearchParams();
  if (city && city !== "Nepal") params.set("city", city);
  if (listingType) params.set("type", listingType);
  if (config.maxPrice) params.set("maxPrice", String(config.maxPrice));

  const findPropertyUrl = `/find-property${params.toString() ? `?${params.toString()}` : ""}`;

  useEffect(() => {
    const id = "gb-seo-faq-jsonld";
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement("script");
      script.id = id;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    };

    script.textContent = JSON.stringify(schema);

    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, [faqItems]);

  return (
    <div className="container py-4">
      <div className="mb-4">
        <h1 className="fw-bold mb-2">{heading}</h1>
        <p className="text-muted mb-3">{lead}</p>
        <button className="btn btn-primary" onClick={() => navigate(findPropertyUrl)}>
          {categoryConfig?.ctaLabel || config.ctaLabel}
        </button>
      </div>

      {isCityHub && (
        <section className="mb-4">
          <h2 className="h5 fw-semibold mb-3">Popular Rental Categories in {city}</h2>
          <div className="row g-2">
            {Object.entries(CATEGORY_MAP).slice(0, 6).map(([slug, cat]) => (
              <div className="col-12 col-md-6 col-lg-4" key={slug}>
                <Link className="btn btn-outline-secondary w-100 text-start" to={`/rent/${String(city).toLowerCase()}/${slug}`}>
                  {cat.label} in {city}
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mb-4">
        <h2 className="h4 fw-semibold mb-3">Top Listings</h2>
        {loading ? (
          <p className="text-muted mb-0">Loading listings...</p>
        ) : results.length === 0 ? (
          <p className="text-muted mb-0">No listings found right now. Try broader filters in Find Property.</p>
        ) : (
          <div className="row g-3">
            {results.map((item) => (
              <div className="col-12 col-md-6 col-lg-4" key={item.id}>
                <div className="card h-100 shadow-sm border-0">
                  <div className="card-body">
                    <h3 className="h6 fw-bold mb-1">{item.name}</h3>
                    <p className="small text-muted mb-2">{item.city}, {item.state}</p>
                    <p className="mb-2">NPR {Number(item.price || 0).toLocaleString()} / month</p>
                    <Link className="btn btn-sm btn-outline-primary" to={`/property/${item.id}`}>
                      View Property
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mb-4">
        <h2 className="h5 fw-semibold mb-2">Frequently Asked Questions</h2>
        <div className="accordion" id="seoFaqAccordion">
          {faqItems.map((item, idx) => (
            <div className="accordion-item" key={item.q}>
              <h3 className="accordion-header" id={`faqHeading${idx}`}>
                <button
                  className={`accordion-button ${idx === 0 ? "" : "collapsed"}`}
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#faqCollapse${idx}`}
                  aria-expanded={idx === 0 ? "true" : "false"}
                  aria-controls={`faqCollapse${idx}`}
                >
                  {item.q}
                </button>
              </h3>
              <div
                id={`faqCollapse${idx}`}
                className={`accordion-collapse collapse ${idx === 0 ? "show" : ""}`}
                aria-labelledby={`faqHeading${idx}`}
                data-bs-parent="#seoFaqAccordion"
              >
                <div className="accordion-body">{item.a}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="h5 fw-semibold mb-2">Related Searches</h2>
        <ul className="mb-0">
          <li><Link to={`/rent/${String(city).toLowerCase()}/gharbhada`}>Gharbhada {city}</Link></li>
          <li><Link to={`/rent/${String(city).toLowerCase()}/flat-bhada`}>Flat Bhada {city}</Link></li>
          <li><Link to={`/rent/${String(city).toLowerCase()}/room-rent`}>Room Rent {city}</Link></li>
          <li><Link to="/cheap-kotha-bhada-near-college">Cheap Kotha Bhada Near College</Link></li>
          {TARGET_CITIES.filter((slug) => slug !== String(city).toLowerCase()).slice(0, 3).map((slug) => (
            <li key={slug}>
              <Link to={`/rent/${slug}/gharbhada`}>Gharbhada {toTitleCase(slug)}</Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export default SeoLandingPage;
